/**
 * Fallback de `rel=prefetch` para navegadores que o ignoram — na prática,
 * Safari/iPhone, que nunca implementou o hint.
 *
 * O servidor já emite os chunks da página do reel como
 * `<link rel="prefetch" data-reel-prefetch>` (ver
 * server/plugins/06.reel-route-prefetch). Onde o hint é suportado
 * (`relList.supports('prefetch')`), este plugin não faz nada — o navegador
 * cuida. Onde não é, busca as MESMAS URLs com `fetch` de prioridade baixa
 * para aquecer o cache HTTP: sem `import()`, sem avaliação de módulo, zero
 * main thread além de iniciar as requisições. No clique, o `import()` da
 * navegação encontra os arquivos no cache.
 *
 * Guardas: só após a PRIMEIRA interação real (Lighthouse é Chromium e nunca
 * interage — dupla proteção), um arquivo por vez, e aborta se a aba esconder.
 * Nunca lança.
 */
import { useEventListener, useTimeoutFn } from '@vueuse/core';

export default defineNuxtPlugin(() => {
	if (!import.meta.client) return;

	try {
		// Navegador honra rel=prefetch → o hint nativo resolve; nada a fazer.
		if (document.createElement('link').relList.supports('prefetch')) return;

		const run = async (): Promise<void> => {
			const hrefs = [
				...document.querySelectorAll<HTMLLinkElement>(
					'link[rel="prefetch"][data-reel-prefetch][href]'
				)
			].map(link => link.href);

			for (const href of hrefs) {
				if (document.hidden) return;
				try {
					await fetch(href, {
						priority: 'low',
						mode: 'cors',
						credentials: 'omit',
						signal: AbortSignal.timeout(10_000)
					} as RequestInit);
				} catch {
					// Aquecimento é melhor-esforço — segue para o próximo arquivo.
				}
			}
		};

		// Safari não tem requestIdleCallback — um respiro fixo tira o warm da
		// rajada de trabalho que acompanha a primeira interação.
		const { start } = useTimeoutFn(() => void run(), 1_500, {
			immediate: false
		});

		// `once` remove cada listener após o próprio disparo; os `stop` limpam
		// os irmãos que não dispararam, para o warm rodar uma única vez.
		const stops = (['touchstart', 'scroll', 'pointermove'] as const).map(
			eventName =>
				useEventListener(
					window,
					eventName,
					() => {
						stops.forEach(stop => stop());
						start();
					},
					{ passive: true, once: true }
				)
		);
	} catch {
		// Nunca deixar o fallback derrubar a aplicação.
	}
});
